<template>
    <span v-if="attribute && attributeValue !== null || attribute?.is_gui_visible_when_null"
        class=" flex flex-wrap items-center"
        :class="{
            'text-xs rounded-xl border border-sn-light-blue px-2.5 py-[3px] bg-sn-superlight-blue': badge,
            'border-sn-light-blue bg-sn-superlight-blue': badge && badgeColour === 'blue',
            'border-sn-light-green bg-sn-superlight-green': badge && badgeColour === 'green',
            'border-sn-light-orange bg-sn-superlight-orange': badge && badgeColour === 'orange',
            'border-sn-midlight-grey bg-sn-light-grey': badge && badgeColour === 'grey',
        }"
    >
        <span
            class="mr-1"
            :class="{'font-semibold': !badge}"
            v-text="attribute.name + ':'"></span>
        <span v-if="attribute.type === 'string'" v-text="attributeValue"></span>
        <span v-else-if="attribute.type === 'select'" v-text="getAttributeOptionValue(attribute.code, attributeValue as number)"></span>
        <template v-else-if="attribute.type === 'boolean'">
            <i v-if="attributeValue" class="fal fa-check"></i>
            <i v-else class="fal fa-times"></i>
        </template>
        <template v-else-if="attribute.code === 'made_to_measure_product_form'">
            <a :href="localFileUrl" class="hover:text-sn-blue" target="_blank">
                <i class="fal fa-download"></i>
                Made to Measure Product Form
            </a>
        </template>
        <template v-else-if="attribute.type === 'image' && fileUrl">
            <ApiImage class="w-full" :src="fileUrl" />
        </template>
        <template v-else>
            <div class="wysiwyg-output" v-html="attributeValue"></div>
        </template>
    </span>
</template>

<style lang="scss">
.wysiwyg-output {
    h3 {
        @apply mt-4 font-semibold text-2xl
    }
    ul li {
        @apply list-disc ml-6;
    }
}

</style>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { PropType } from 'vue';
import type { AttributeData } from '@/types/attributes';
import {useAttributes} from "@/composables/useAttributes";
import ApiImage from "@/components/core/ApiImage.vue";

import {useFiles} from "@/composables/useFiles";
import {useApi} from "~/composables/useApi";

const { getFile } = useFiles();
const { getAttributeOptionValue } = await useAttributes();
const localFileUrl = ref('');
// Reactive file loading for image attributes
const fileUrl = ref<string | null>(null);

const props = defineProps({
    attribute: {
        type: Object as PropType<AttributeData>,
        required: false
    },
    attributeValue: {
        type: [String, Number, Boolean, null],
        default: null
    },
    badge: {
        type: Boolean,
        default: false,
    },
    badgeColour: {
        type: String as PropType<'blue' | 'green' | 'orange' | 'grey'>,
        default: 'blue',
    },
    isLast: {
        type: Boolean as PropType<boolean>,
        default: false
    },
    showLabel: {
        type: Boolean as PropType<boolean>,
        default: true
    }
});

watch(() => props.attributeValue, async (newValue) => {
    if (props.attribute?.type === 'image' && newValue && typeof newValue === 'number') {
        try {
            const file = await getFile(newValue);
            fileUrl.value = file?.url || null;
        } catch (error) {
            console.error('Error loading file:', error);
            fileUrl.value = null;
        }
    } else {
        fileUrl.value = null;
    }
}, { immediate: true });

// const attributeComponent = computed(() => {
//     const attributeType = props.type.charAt(0).toUpperCase() + props.type.slice(1);
//     return () => import(`./Attribute${attributeType}`);
// });

watch(() => fileUrl.value, () => {
    if(typeof fileUrl.value === 'string' && fileUrl.value.includes('.pdf')) {
        getLocalFileUrl(fileUrl.value);
    }
})

async function getLocalFileUrl(fileUrl: string) {
    const { data: image, loading, fetch } = useApi<Blob>({
        url: fileUrl?.substring(1), // gets rid of initial "/" as both baseUrl and image.url include a "/"
        method: 'GET',
    });

    await fetch();

    if(image.value) {
        localFileUrl.value = URL.createObjectURL(image.value);
    }
}

</script>

<style scoped>

</style>
